<template>
  <div class="pagina">
    <!-- Cabecera de identidad: quien edita varias empresas necesita saber de
         un vistazo en cual esta parado. Antes solo decia "Editar empresa" y
         era identico para todas. -->
    <div class="page-header">
      <div class="identidad">
        <div class="miniatura">
          <v-img
            v-if="portada"
            :src="portada"
            cover
            class="miniatura-img"
          />
          <v-icon
            v-else
            size="24"
            class="icono-sin-foto"
          >
            mdi-store-outline
          </v-icon>
        </div>

        <div class="identidad-texto">
          <div class="etiqueta-editando">
            <v-icon size="12">
              mdi-pencil
            </v-icon>
            Editando empresa
          </div>
          <h2 class="nombre-empresa">
            {{ myCompanyData.company || 'Cargando...' }}
          </h2>
          <div class="meta-empresa">
            <span v-if="ubicacion">
              <v-icon size="13">mdi-map-marker-outline</v-icon>
              {{ ubicacion }}
            </span>
            <span>
              <v-icon size="13">mdi-eye-outline</v-icon>
              {{ myCompanyData.views || 0 }} vistas
            </span>
          </div>
        </div>
      </div>

      <!-- Ahora que guardar ya no devuelve a la lista, hace falta una salida -->
      <v-btn
        variant="text"
        size="small"
        class="volver"
        @click="$router.push({ name: 'myCompanysView' })"
      >
        <v-icon size="17">
          mdi-arrow-left
        </v-icon>
        Mis empresas
      </v-btn>
    </div>

    <!-- ESCRITORIO: pestanas de toda la vida, que ahi si caben -->
    <v-tabs
      v-model="pestana"
      color="primary"
      density="compact"
      show-arrows
      class="pestanas-escritorio"
    >
      <v-tab
        v-for="seccion in secciones"
        :key="seccion.valor"
        :value="seccion.valor"
      >
        <v-icon
          start
          size="18"
        >
          {{ seccion.icono }}
        </v-icon>
        {{ seccion.nombre }}
      </v-tab>
    </v-tabs>

    <!-- CELULAR: rejilla 2x2. Las cuatro secciones se ven completas de una,
         sin barra que desplazar y con un area de toque grande. -->
    <div class="pestanas-celular">
      <button
        v-for="seccion in secciones"
        :key="seccion.valor"
        type="button"
        class="seccion-movil"
        :class="{ activa: pestana === seccion.valor }"
        @click="pestana = seccion.valor"
      >
        <v-icon size="20">
          {{ seccion.icono }}
        </v-icon>
        <span>{{ seccion.nombre }}</span>
      </button>
    </div>

    <!-- touch en false: por defecto v-window cambia de pestana al arrastrar de
         lado, y en celular eso se dispara solo al desplazar el contenido. Era
         justo lo que hacia que se saltara de seccion sin querer. -->
    <v-window
      v-model="pestana"
      class="ventana-pestanas"
      :touch="false"
    >
      <v-window-item value="informacion">
        <v-card class="card">
          <!-- Estado de la publicacion. Lo primero que ve el dueno al entrar:
               antes tenia que ir al perfil publico para darse cuenta de que su
               pagina estaba caida. -->
          <div
            v-if="vigente === null"
            class="estado-publicacion estado-cargando"
          >
            <v-progress-circular
              indeterminate
              size="18"
              width="2"
              color="primary"
            />
            <span>Comprobando el estado de tu publicación...</span>
          </div>

          <div
            v-else-if="vigente"
            class="estado-publicacion estado-en-linea"
          >
            <v-icon size="20">
              mdi-check-circle
            </v-icon>
            <div class="estado-texto">
              <strong>Tu empresa está en línea</strong>
              <span>Cualquiera puede encontrarla en el directorio y en las búsquedas.</span>
            </div>
            <v-btn
              variant="text"
              size="small"
              class="estado-accion"
              @click="$router.push({ name: 'ViewCompanyView', params: { id: companyId } })"
            >
              Ver cómo la ven
            </v-btn>
          </div>

          <div
            v-else
            class="estado-publicacion estado-caida"
          >
            <v-icon size="20">
              mdi-alert-circle
            </v-icon>
            <div class="estado-texto">
              <strong>Tu empresa no se está viendo</strong>
              <span>
                Su hospedaje no está vigente, así que no aparece en el directorio
                ni en las búsquedas. Nadie más puede abrir su página.
              </span>
            </div>
            <div class="estado-botones">
              <v-btn
                color="red"
                variant="flat"
                size="small"
                :loading="pagando"
                @click="pagarMes"
              >
                <v-icon start size="17">
                  mdi-credit-card-outline
                </v-icon>
                Pagar un mes · {{ precioMesEnPesos }}
              </v-btn>
              <v-btn
                variant="text"
                size="small"
                class="estado-accion"
                @click="$router.push({ name: 'MyPaymentsView' })"
              >
                Ver mis pagos
              </v-btn>
            </div>
          </div>

          <v-form
            ref="companyForm"
            @submit.prevent="updateCompany"
          >
            <div class="seccion-label">
              Datos básicos
            </div>
            <v-text-field
              v-model="myCompanyData.company"
              :rules="[v => !!v || 'El nombre es obligatorio']"
              class="fg-blue mb-2"
              label="Nombre"
              variant="outlined"
              density="compact"
            />
            <v-textarea
              v-model="myCompanyData.description"
              class="fg-blue mb-2"
              label="Descripción"
              variant="outlined"
              density="compact"
              rows="3"
              :counter="1200"
              maxlength="1200"
            />

            <div class="seccion-label">
              Ubicación
            </div>
            <div class="grid-2">
              <v-text-field
                v-model="myCompanyData.country"
                class="fg-blue"
                label="País"
                variant="outlined"
                density="compact"
              />
              <v-text-field
                v-model="myCompanyData.city"
                class="fg-blue"
                label="Ciudad"
                variant="outlined"
                density="compact"
              />
            </div>
            <v-text-field
              v-model="myCompanyData.location"
              class="fg-blue mb-2 mt-2"
              label="Dirección"
              variant="outlined"
              density="compact"
            />

            <div class="seccion-label">
              Contacto
            </div>
            <!-- Sin :rules a proposito: el formulario nunca llama a
                 validate(), asi que la regla pintaba el error pero dejaba
                 guardar igual. El bloqueo real esta en updateCompany. -->
            <v-text-field
              v-model="myCompanyData.phone"
              :error-messages="errorFonoPrincipal"
              type="tel"
              inputmode="tel"
              class="fg-blue mb-2"
              label="Teléfono"
              variant="outlined"
              density="compact"
              maxlength="30"
            />

            <v-switch
              v-model="myCompanyData.receivesWhatsapp"
              class="mb-1"
              color="primary"
              density="compact"
              label="Recibimos mensajes por WhatsApp"
              hide-details
            />

            <!-- El numero solo aparece si lo activa: un campo pidiendo datos
                 que no va a usar nadie es ruido en un formulario que ya es
                 largo. Si lo apaga, el numero se borra para no dejar un dato
                 colgado que despues nadie sabe si vale. -->
            <v-text-field
              v-if="myCompanyData.receivesWhatsapp"
              v-model="myCompanyData.whatsapp"
              :error-messages="errorWhatsapp"
              type="tel"
              inputmode="tel"
              class="fg-blue mb-2 mt-2"
              label="Número de WhatsApp"
              variant="outlined"
              density="compact"
              maxlength="30"
              hint="Con indicativo si es de otro país. Ej: 3204720783"
              persistent-hint
            />

            <div class="seccion-label">
              Reservas
            </div>
            <v-switch
              v-model="myCompanyData.acceptsReservations"
              class="mb-2"
              color="primary"
              density="compact"
              label="Esta empresa acepta reservas"
              hide-details
            />

            <v-btn
              type="submit"
              color="primary"
              block
              :loading="guardando"
            >
              Guardar cambios
            </v-btn>
          </v-form>

          <v-divider class="my-5" />

          <!-- El QR no es un campo del formulario, por eso va FUERA del
               v-form: dentro, Enter en cualquier campo podria dispararlo en
               vez de guardar. -->
          <div class="seccion-label">
            Código QR
          </div>
          <div class="bloque-qr">
            <div class="texto-qr">
              <p class="ayuda-qr">
                Imprímelo y pégalo en tu local. Quien lo escanee llega directo
                a la página de tu empresa.
              </p>
              <code class="url-qr">{{ urlPublica }}</code>
            </div>
            <v-btn
              color="primary"
              variant="flat"
              :loading="generandoQr"
              @click="descargarCodigoQr"
            >
              <v-icon start>
                mdi-qrcode-scan
              </v-icon>
              Descargar QR
            </v-btn>
          </div>

          <div class="seccion-label mt-5">
            Carta
          </div>
          <div class="bloque-qr">
            <div class="texto-qr">
              <p class="ayuda-qr">
                Una hoja con tus productos y sus precios, lista para imprimir.
                <strong>Esta opción es adecuada para restaurantes.</strong>
              </p>
            </div>
            <v-btn
              color="primary"
              variant="flat"
              :loading="generandoCarta"
              @click="descargarLaCarta"
            >
              <v-icon start>
                mdi-file-document-outline
              </v-icon>
              Descargar carta
            </v-btn>
          </div>
        </v-card>
      </v-window-item>

      <v-window-item value="imagenes">
        <v-card class="card">
          <GaleriaEditable
            :imagenes="imagesCompany"
            ayuda="La primera es la que se ve en el listado de empresas. Son lo primero que van a ver tus clientes."
            @agregar="dialogImagenOpen = true"
            @eliminar="deleteImage"
          />
        </v-card>
      </v-window-item>

      <v-window-item value="productos">
        <PanelEdicionProductos
          v-if="companyId"
          :id-company="companyId"
          :con-hospedaje="vigente"
        />
      </v-window-item>

      <v-window-item value="reservas">
        <v-card class="card">
          <!-- Sin hospedaje vigente la pagina publica no se ve, asi que nadie
               puede reservar: el panel mostraria una agenda que no se puede
               llenar. Mientras se comprueba no se pinta ni el panel ni el
               candado, para no ensenar la agenda y taparla un segundo
               despues. -->
          <div
            v-if="vigente === null"
            class="bloqueo-cargando"
          >
            <v-progress-circular
              indeterminate
              size="22"
              width="2"
              color="primary"
            />
            <span>Comprobando el estado de tu publicación...</span>
          </div>

          <div
            v-else-if="!vigente"
            class="bloqueo"
          >
            <div class="bloqueo-icono">
              <v-icon size="30">
                mdi-lock-outline
              </v-icon>
            </div>
            <h3 class="bloqueo-titulo">
              Las reservas necesitan un pago vigente
            </h3>
            <p class="bloqueo-texto">
              Mientras tu hospedaje no esté al día tu página no se ve, así que
              nadie puede reservar. Paga un mes y esta sección se activa sola.
            </p>
            <div class="bloqueo-botones">
              <v-btn
                color="primary"
                variant="flat"
                :loading="pagando"
                @click="pagarMes"
              >
                <v-icon
                  start
                  size="17"
                >
                  mdi-credit-card-outline
                </v-icon>
                Pagar un mes · {{ precioMesEnPesos }}
              </v-btn>
              <v-btn
                variant="text"
                class="estado-accion"
                @click="$router.push({ name: 'MyPaymentsView' })"
              >
                Ver mis pagos
              </v-btn>
            </div>
          </div>

          <PanelReservas
            v-else-if="companyId"
            :id-empresa="companyId"
          />
        </v-card>
      </v-window-item>
    </v-window>

    <v-dialog
      v-model="dialogImagenOpen"
      max-width="620"
      :persistent="subiendoImagenes"
    >
      <div style="text-align: -webkit-center;width: 100%;">
        <EntryLoalImage
          ref="entradaImagenes"
          multiple
          :subiendo="subiendoImagenes"
          :progreso="progresoSubida"
          @onImageSelected="saveImage"
        />
      </div>
    </v-dialog>

    <!-- Aviso flotante, por encima de la pagina y no dentro del formulario.
         Los errores se quedan hasta que los cierren, porque hay algo que leer
         y casi siempre algo que reintentar; los de exito se van solos. -->
    <v-snackbar
      v-model="snackbar"
      :timeout="colorSnackbar === 'error' ? -1 : 4000"
      :color="colorSnackbar"
      location="top"
      class="aviso-flotante"
    >
      <div class="aviso-contenido">
        <v-icon size="22">
          {{ colorSnackbar === 'error' ? 'mdi-alert-circle' : 'mdi-check-circle' }}
        </v-icon>
        <span>{{ mensajeSnackbar }}</span>
      </div>

      <!-- Boton explicito y no la prop closable: en esta version de Vuetify
           closable no pinta nada, y sin salida el aviso de error se quedaria
           para siempre porque su timeout es -1. -->
      <template #actions>
        <v-btn
          variant="text"
          size="small"
          @click="snackbar = false"
        >
          Cerrar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>
<script>
import apisCompanys from '@/apis/apisCompanys';
import apisUsers from '@/apis/apisUsers';
import PanelEdicionProductos from '@/components/PanelEdicionProductos.vue'
import EntryLoalImage from '@/components/Fields/EntryLoalImage.vue'
import PanelReservas from '@/components/Reservas/PanelReservas.vue'
import GaleriaEditable from '@/components/Fields/GaleriaEditable.vue'
import { mensajeDeError } from '@/utils/errores'
import { descargarQr } from '@/utils/qr'
import { descargarBasicoCartaPdf } from '@/utils/carta'
import { limpiarTelefono, errorTelefono } from '@/utils/telefono'
import apisProducts from '@/apis/apisProducts'
import apisPAYS from '@/apis/apisPAYS'
import { confirmarPagoWompi } from '@/utils/pagoWompi'
import { precioMesHospedaje, enPesos, PRECIO_MES_RESPALDO } from '@/utils/configuracion'

export default {
  name: 'EditCompanyView',
  components: { PanelEdicionProductos, EntryLoalImage, PanelReservas, GaleriaEditable },
  data(){
    return {
      pestana: 'informacion',
      // Una sola lista para las dos formas de navegar: si se agrega una
      // seccion aparece en escritorio y en celular sin tocar nada mas.
      secciones: [
        { valor: 'informacion', nombre: 'Información', icono: 'mdi-information-outline' },
        { valor: 'imagenes', nombre: 'Imágenes', icono: 'mdi-image-multiple' },
        { valor: 'productos', nombre: 'Productos', icono: 'mdi-package-variant' },
        { valor: 'reservas', nombre: 'Reservas', icono: 'mdi-calendar-check' }
      ],
      companyId: null,
      myCompanyData: {
        "idCompany": null,
        "idUser": null,
        "company": "",
        "country": "",
        "city": "",
        "description": "",
        "location": "",
        "phone": "",
        "acceptsReservations": false,
        "receivesWhatsapp": false,
        "whatsapp": "",
        "views": null
      },
      imagesCompany: [
        //idImagen and pathBase64
      ],
      dialogImagenOpen: false,
      subiendoImagenes: false,
      progresoSubida: '',
      snackbar: false,
      mensajeSnackbar: '',
      colorSnackbar: 'success',
      guardando: false,
      // null mientras se comprueba: asi no se muestra "no vigente" un segundo
      // antes de saberlo, que asustaria sin motivo
      vigente: null,
      // Lo administra Settings. Arranca en el respaldo para que el boton no
      // aparezca un instante sin precio.
      precioMes: PRECIO_MES_RESPALDO,
      pagando: false,
      generandoQr: false,
      generandoCarta: false
    }
  },
  watch: {
    // Se limpia mientras se escribe: asi las letras no llegan al backend ni
    // siquiera si alguien las pega desde el portapapeles. La validacion de
    // formato sigue estando en los dos lados, esto solo evita el viaje.
    'myCompanyData.phone'(valor){
      const limpio = limpiarTelefono(valor)
      if (limpio !== valor) {
        this.myCompanyData.phone = limpio
      }
    },
    'myCompanyData.whatsapp'(valor){
      const limpio = limpiarTelefono(valor)
      if (limpio !== valor) {
        this.myCompanyData.whatsapp = limpio
      }
    }
  },
  computed: {
    precioMesEnPesos(){
      return enPesos(this.precioMes)
    },
    /** Primera imagen de la empresa, reutilizando las que ya carga loadImages */
    portada(){
      return this.imagesCompany.length > 0 ? this.imagesCompany[0].pathBase64 : null
    },
    ubicacion(){
      return [this.myCompanyData.city, this.myCompanyData.country].filter(Boolean).join(', ')
    },
    errorFonoPrincipal(){
      // Mientras getCompany no responde el campo esta vacio, y sin esta
      // guarda salia "El telefono es obligatorio" en rojo en cada carga,
      // antes de que el usuario hubiera tocado nada
      if (!this.myCompanyData.idCompany) {
        return []
      }
      return errorTelefono(this.myCompanyData.phone)
    },
    /**
     * Si dice que recibe WhatsApp, el numero es obligatorio: activar la
     * casilla y dejarlo vacio es peor que no activarla, porque promete un
     * canal que no existe.
     */
    errorWhatsapp(){
      if (!this.myCompanyData.receivesWhatsapp) {
        return []
      }

      return errorTelefono(this.myCompanyData.whatsapp, {
        mensajeVacio: 'Escribe el número o desactiva la casilla'
      })
    },
    /**
     * Lo que codifica el QR. Sale de location.origin y no de una constante
     * para que en local apunte a local y en produccion a produccion.
     */
    urlPublica(){
      return `${window.location.origin}/viewCompany/${this.companyId}`
    }
  },
  created(){
    apisUsers.isValid()
      .then(isValid => {
        if (!isValid){
          this.$router.push('/')
        }
      })

    // Number y no el texto crudo de la ruta: PayController hace un cast
    // directo (Integer) sobre lo que le llega, asi que un "11" en vez de 11
    // revienta con "String cannot be cast to Integer". Ademas
    // PanelEdicionProductos declara la prop como Number.
    this.companyId = Number(this.$route.params.id)

    apisCompanys.getCompany(this.companyId)
      .then(company => {
        delete company.user
        this.myCompanyData = company
      })

    apisCompanys.isEnabled(this.companyId)
      .then(vigente => {
        this.vigente = vigente
      })
      // Si no se puede comprobar, se asume que esta bien: decirle que su
      // pagina esta caida cuando en realidad fallo la consulta es peor que
      // no decir nada
      .catch(() => {
        this.vigente = true
      })
    this.loadImages()

    precioMesHospedaje().then(precio => {
      this.precioMes = precio
    })
  },
  methods: {
    /**
     * Guarda y SE QUEDA en la vista. Antes se iba a "Mis empresas" apenas
     * respondia el servidor: el usuario perdia el sitio donde estaba y ni
     * siquiera veia si el cambio habia entrado.
     *
     * Tampoco tenia catch, asi que un error del servidor no hacia nada en
     * absoluto: el boton se quedaba quieto y no aparecia ningun aviso.
     */
    updateCompany(){
      const problema = this.errorFonoPrincipal[0] || this.errorWhatsapp[0]

      if (problema) {
        this.avisar(problema, 'error')
        return
      }

      // Apagar la casilla borra el numero: si no, queda un dato guardado que
      // nadie sabe si sigue vigente la proxima vez que lo enciendan.
      if (!this.myCompanyData.receivesWhatsapp) {
        this.myCompanyData.whatsapp = null
      }

      this.guardando = true

      apisCompanys.updateCompany(this.myCompanyData)
        .then(() => {
          this.avisar('Los cambios se guardaron', 'success')
        })
        .catch(error => {
          this.avisar(mensajeDeError(error, 'No se pudieron guardar los cambios'), 'error')
        })
        .finally(() => {
          this.guardando = false
        })
    },
    /**
     * Recibe una o varias imagenes en base64. Se suben de a una y no en
     * paralelo: cada una viaja como base64 dentro del JSON y mandarlas todas
     * juntas satura el servidor.
     */
    async saveImage(imagenes){
      const lista = Array.isArray(imagenes) ? imagenes : [imagenes]

      if (lista.length === 0){
        return
      }

      this.subiendoImagenes = true
      let fallidas = 0

      for (let i = 0; i < lista.length; i++){
        this.progresoSubida = `Subiendo ${i + 1} de ${lista.length}...`

        try {
          await apisCompanys.imageSave(lista[i], this.companyId)
        } catch (err) {
          console.error("Error saving image:", err)
          fallidas++
        }
      }

      this.subiendoImagenes = false
      this.progresoSubida = ''
      this.loadImages()

      const subidas = lista.length - fallidas

      if (fallidas === 0){
        this.avisar(`${subidas} imagen(es) subida(s)`, 'success')
        if (this.$refs.entradaImagenes){
          this.$refs.entradaImagenes.limpiar()
        }
        this.dialogImagenOpen = false
      } else {
        // El dialogo se queda abierto para poder reintentar con las que fallaron
        this.avisar(
          `${subidas} subida(s), ${fallidas} fallaron. Puede que sean muy grandes o de un formato no soportado.`,
          'error'
        )
      }
    },
    /**
     * Paga un mes de hospedaje sin salir de la vista.
     *
     * Es el MISMO flujo de MyPaymentsView y usa los mismos endpoints: se pide
     * la firma a /PAYS/PayOneMonth y se abre el widget embebido de Wompi. No
     * se toco nada del codigo de pagos, solo se llama desde aqui tambien,
     * porque este es el sitio donde el dueno se entera de que esta caido.
     */
    pagarMes(){
      this.pagando = true

      apisPAYS.payOneMonth(this.companyId)
        .then(data => {
          const checkout = new window.WidgetCheckout({
            currency: data.currency,
            amountInCents: data.amountInCents,
            reference: data.reference,
            publicKey: data.publicKey,
            signature: { integrity: data.signature }
          })

          checkout.open(async result => {
            const { estado, mensaje, color } = await confirmarPagoWompi(result)
            this.pagando = false
            // Sin alert(): bloquea la pagina y no se puede leer despues
            this.avisar(mensaje, color)

            if (estado === 'APPROVED') {
              this.revisarVigencia()
            }
          })
        })
        .catch(error => {
          this.pagando = false
          this.avisar(mensajeDeError(error, 'No se pudo iniciar el pago'), 'error')
        })
    },
    /**
     * Vuelve a preguntar si ya esta vigente. Quien confirma el pago es el
     * webhook de Wompi, que puede tardar unos segundos, asi que si todavia
     * sale que no, se dice y no se deja al usuario pensando que no sirvio.
     */
    revisarVigencia(){
      apisCompanys.isEnabled(this.companyId)
        .then(vigente => {
          this.vigente = vigente

          if (!vigente) {
            this.avisar(
              'El pago quedó registrado. La publicación se activa en unos minutos.',
              'success'
            )
          }
        })
        .catch(() => {})
    },
    async descargarCodigoQr(){
      this.generandoQr = true

      try {
        await descargarQr(this.urlPublica, this.myCompanyData.company)
        this.avisar('QR descargado', 'success')
      } catch (error) {
        this.avisar(mensajeDeError(error, 'No se pudo generar el QR'), 'error')
      } finally {
        this.generandoQr = false
      }
    },
    /**
     * Los productos no se guardan en esta vista (los maneja su propia
     * pestana), asi que se piden al momento de generar la carta. Es una sola
     * peticion y solo cuando el usuario pulsa el boton.
     */
    async descargarLaCarta(){
      this.generandoCarta = true

      try {
        const productos = await apisProducts.getProductsCompany(this.companyId)

        // La foto de cada producto va aparte del listado, asi que hay que
        // pedirla una por una. Se hace aqui y no dentro del util para que
        // ese siga sin depender de las APIs. Si alguna falla, ese producto
        // sale sin foto en vez de tumbar la carta entera.
        await Promise.all(
          (productos || []).map(async producto => {
            const imagenes = await apisProducts
              .ImagesProducts(producto.idProduct)
              .catch(() => [])
            producto.imagen = imagenes && imagenes.length > 0 ? imagenes[0].pathBase64 : null
          })
        )

        await descargarBasicoCartaPdf(this.myCompanyData, this.imagesCompany, productos)
        this.avisar('Carta descargada', 'success')
      } catch (error) {
        this.avisar(mensajeDeError(error, 'No se pudo generar la carta'), 'error')
      } finally {
        this.generandoCarta = false
      }
    },
    avisar(mensaje, color){
      this.mensajeSnackbar = mensaje
      this.colorSnackbar = color
      this.snackbar = true
    },
    loadImages () {
      apisCompanys.ImagesCompany(this.companyId)
        .then(images =>{
          this.imagesCompany = images
        })
    },
    deleteImage(idImage){
      apisCompanys.deleteImage(idImage)
        .then(isDelete => {
          if (isDelete){
            this.loadImages()
          }
        })
    }
  }
}
</script>
<style scoped>
/* ESTADO DE LA PUBLICACION */
.estado-publicacion {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 18px;
  font-size: 0.86rem;
}
.estado-texto {
  flex: 1 1 220px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.estado-texto strong {
  font-size: 0.94rem;
}
.estado-botones {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.estado-accion {
  flex: 0 0 auto;
  text-transform: none;
  letter-spacing: 0;
}

.estado-cargando {
  align-items: center;
  background: #f2f5f9;
  color: #667085;
}
.estado-en-linea {
  background: #e6f5ee;
  color: #0f7a52;
}
.estado-en-linea .estado-accion {
  color: #0f7a52;
}
/* La caida grita mas a proposito: es la unica que pide dinero */
.estado-caida {
  background: #fdeceb;
  color: #a8271f;
  border: 1px solid #f2c4c0;
  flex-wrap: wrap;
}
.estado-caida .estado-accion {
  color: #a8271f;
}

@media (max-width: 560px) {
  .estado-publicacion {
    flex-wrap: wrap;
  }
  .estado-accion {
    margin-left: 30px;
  }
}

/* SECCION BLOQUEADA POR FALTA DE PAGO */
.bloqueo-cargando {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 46px 12px;
  color: #667085;
  font-size: 0.88rem;
}

.bloqueo {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 10px;
  padding: 40px 18px 44px;
}
.bloqueo-icono {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fdeceb;
  color: #a8271f;
}
.bloqueo-titulo {
  margin: 4px 0 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: #1a1a1a;
}
.bloqueo-texto {
  margin: 0;
  max-width: 420px;
  font-size: 0.88rem;
  line-height: 1.45;
  color: #667085;
}
.bloqueo-botones {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 6px;
}

/* BLOQUE DEL QR */
.bloque-qr {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border: 1px dashed #c4cbd4;
  border-radius: 10px;
  background: #fafbfc;
}
.texto-qr {
  min-width: 0;
  flex: 1 1 220px;
}
.ayuda-qr {
  margin: 0 0 6px;
  font-size: 0.8rem;
  color: #667085;
  line-height: 1.35;
}
.url-qr {
  display: block;
  font-size: 0.72rem;
  color: #1976d2;
  background: #eef4fb;
  padding: 3px 7px;
  border-radius: 5px;
  word-break: break-all;
}

/* Aviso flotante de guardado: icono y texto en una linea */
.aviso-contenido {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 500;
}

.pagina {
  max-width: 1100px;
  margin: 0 auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.ventana-pestanas {
  /* v-window recorta por defecto y eso corta las sombras de los campos */
  overflow: visible;
}

/* NAVEGACION DE SECCIONES
   En escritorio manda v-tabs. En celular se cambia por una rejilla 2x2:
   con la barra de pestanas habia que desplazarla de lado para llegar a
   Reservas, y ese mismo gesto terminaba cambiando de seccion sin querer. */
.pestanas-celular {
  display: none;
}

@media screen and (max-width: 599px) {
  .pestanas-escritorio {
    display: none;
  }
  .pestanas-celular {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }
}

.seccion-movil {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  /* Area de toque comoda para el pulgar */
  min-height: 62px;
  padding: 8px 6px;
  border: 1px solid #dfe3e8;
  border-radius: 10px;
  background: #fff;
  color: #667085;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.seccion-movil:active {
  background: #f2f5f9;
}
.seccion-movil.activa {
  border-color: #1976d2;
  background: #e8f1fb;
  color: #1976d2;
}
/* Tarjeta de identidad en el azul de Bahiol, el mismo #1976d2 de la marca.
   Va en color y no en blanco a proposito: es lo unico de la pagina que dice
   QUE empresa se esta editando, y con las tarjetas blancas de abajo se
   confundiria con una seccion mas del formulario. */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  /* "to bottom right" y no 135deg: con un angulo fijo el degradado se reparte
     por el ancho y en una caja tan alargada casi no se nota el cambio. La
     palabra clave hace que vaya de la esquina superior izquierda a la inferior
     derecha de verdad, sea cual sea la proporcion de la tarjeta.
     Del azul claro al #1976d2 de la marca y cierra en el azul oscuro que
     enlaza con el negro del encabezado. */
  background: linear-gradient(to bottom right, #42a5f5 0%, #1976d2 45%, #0d3f75 100%);
  color: #fff;
  box-shadow: 0 3px 12px rgba(25, 118, 210, 0.3);
}
.identidad {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.miniatura {
  flex: 0 0 auto;
  width: 52px;
  height: 52px;
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
}
.miniatura-img {
  width: 100%;
  height: 100%;
}
.icono-sin-foto {
  color: rgba(255, 255, 255, 0.7);
}
.identidad-texto {
  min-width: 0;
}
.etiqueta-editando {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}
.nombre-empresa {
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.meta-empresa {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  font-size: 0.76rem;
  color: rgba(255, 255, 255, 0.86);
}
.meta-empresa span {
  display: flex;
  align-items: center;
  gap: 4px;
}
.volver {
  text-transform: none;
  letter-spacing: 0;
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
  gap: 4px;
}
.volver:hover {
  background: rgba(255, 255, 255, 0.24);
}
.card {
  padding: 14px 16px;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.seccion-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #1976d2;
  margin: 10px 0 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e3e3e3;
}
.seccion-label:first-of-type {
  margin-top: 0;
}
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

@media screen and (max-width: 500px) {
  .grid-2 {
    grid-template-columns: 1fr;
  }
}
</style>